// API de Worktime Service para Vue 3
// Migrado desde worktime.js
// Proporciona acceso tipado al backend REST

import type { AxiosError, AxiosRequestConfig } from "axios";
import discovery from "../discovery";
import { HTTP, UTILS } from "../http-common";
import gwtApi from "./gwt/gwtAPI";

/**
 * Callbacks genéricos
 */
type SuccessCallback<T = any> = (data: T) => void;
type ErrorCallback = (error: AxiosError) => void;

/**
 * API cliente para comunicación con Worktime Service
 */
export class WorktimeApi {
  /**
   * Obtiene los headers comunes para todas las peticiones
   * Público para permitir que APIs modulares lo reutilicen
   */
  public getCommonHeaders(): AxiosRequestConfig {
    const headers: any = {
      authorization: `Bearer ${window.kc_token || ""}`,
    };

    // Soporte para modo Kiosko
    const kioskEmployeeId = window.localStorage.getItem(
      "worktime.kiosk.employee.id"
    );
    if (kioskEmployeeId) {
      headers["X-KIOSK-EMPLOYEE"] = kioskEmployeeId;
      headers["X-DEVICE-TYPE"] = "KIOSKO";
    } else {
      headers["X-DEVICE-TYPE"] = gwtApi.isApp() ? "APP" : "WEB";
    }

    // Soporte para múltiples perfiles (cambio de empresa)
    const clientCompany = window.localStorage.getItem(
      "worktime.client.company.id"
    );
    if (clientCompany) {
      headers["x-client-company"] = clientCompany;
    }

    // Fix para Internet Explorer (legacy)
    if (/MSIE|Trident/.test(window.navigator.userAgent)) {
      headers["Pragma"] = "no-cache";
      headers["Cache-Control"] = "no-cache, no-store";
    }

    return { headers };
  }

  /**
   * GET genérico
   */
  genericGet<T = any>(
    service: string,
    onSuccess: SuccessCallback<T>,
    onError: ErrorCallback
  ): void {
    HTTP.get(
      `${discovery.worktime_service_url}${service}`,
      this.getCommonHeaders()
    )
      .then((response) => {
        onSuccess(response.data);
      })
      .catch((error) => {
        onError(error);
      });
  }

  /**
   * POST genérico
   */
  genericPost<T = any>(
    service: string,
    data: any,
    onSuccess: SuccessCallback<T>,
    onError: ErrorCallback
  ): void {
    const config = this.getCommonHeaders();

    // Si es FormData, dejar que el navegador establezca el Content-Type automáticamente
    // (incluye el boundary necesario para multipart/form-data)
    if (data instanceof FormData) {
      // No establecer Content-Type, axios lo hará automáticamente
      // El navegador añadirá: Content-Type: multipart/form-data; boundary=----WebKitFormBoundary...
    }

    HTTP.post(`${discovery.worktime_service_url}${service}`, data, config)
      .then((response) => {
        onSuccess(response.data);
      })
      .catch((error) => {
        onError(error);
      });
  }

  /**
   * PUT genérico
   */
  genericPut<T = any>(
    service: string,
    data: any,
    onSuccess?: SuccessCallback<T>,
    onError?: ErrorCallback
  ): void {
    HTTP.put(
      `${discovery.worktime_service_url}${service}`,
      data,
      this.getCommonHeaders()
    )
      .then((response) => {
        if (onSuccess) onSuccess(response.data);
      })
      .catch((error) => {
        if (onError) onError(error);
      });
  }

  /**
   * DELETE genérico
   */
  genericDelete<T = any>(
    service: string,
    onSuccess: SuccessCallback<T>,
    onError: ErrorCallback
  ): void {
    HTTP.delete(
      `${discovery.worktime_service_url}${service}`,
      this.getCommonHeaders()
    )
      .then((response) => {
        onSuccess(response.data);
      })
      .catch((error) => {
        onError(error);
      });
  }

  /**
   * Obtiene un valor del contexto
   */
  getContextValue(key: string): string {
    const context = (window as any).tweemContext;
    return context ? context[key] : "";
  }

  // ============================================
  // MÉTODOS COMUNES
  // ============================================

  /**
   * Obtiene el staff (empleados)
   */
  getStaff(onSuccess: SuccessCallback, onError: ErrorCallback): void {
    this.genericGet("admin/staff", onSuccess, onError);
  }

  /**
   * Ejecuta una query de analytics
   */
  executeQuery(
    data: any,
    onSuccess: SuccessCallback,
    onError: ErrorCallback
  ): void {
    this.genericPost("analytics/executeQuery", data, onSuccess, onError);
  }

  /**
   * Obtiene las empresas
   */
  getCompanies(onSuccess: SuccessCallback, onError: ErrorCallback): void {
    this.genericGet("admin/companies", onSuccess, onError);
  }

  /**
   * Obtiene los centros de trabajo por ID de empresa
   */
  getWorkplacesByCompanyId(
    companyId: number,
    onSuccess: SuccessCallback,
    onError: ErrorCallback
  ): void {
    this.genericGet(
      `admin/workplaces-by-company-id/${companyId}`,
      onSuccess,
      onError
    );
  }

  // ============================================
  // UTILIDADES
  // ============================================

  /**
   * Descarga un documento
   */
  downloadDocument = UTILS.downloadDocument;
}

// Exportar instancia única (singleton)
export const worktimeApi = new WorktimeApi();

// Exportar como default para compatibilidad
export default worktimeApi;
